import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import {
    SaveButton,
    DeleteWithConfirmButton,
    Toolbar,
    Notification,
    Show,
    SimpleShowLayout,
    TextField,
    useNotify
} from "react-admin";
import QRCode from "react-qr-code";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import { ThemeProvider } from "@material-ui/styles";
import { useRecordContext } from "ra-core";
import CopyToClipboard from "react-copy-to-clipboard";
import { FaCheck, FaCopy, FaDownload } from "react-icons/fa";
import customTheme from "../../customTheme";
import baseURL from "../../settings";


const useStyles = makeStyles(() => ({
    root: {
        width: "90%",
    },
    toolbar: {
        justifyContent: "space-between",
    },
    grouping: {
        border: "1px solid lightgray",
        borderRadius: "3px",
        margin: "1% auto",
    },
    warnLabel: {
        fontSize: "10px",
        color: "red",
        display: "block",
    },
    displayFlex: {
        display: "flex",
    },
    equalWidth: {
        maxWidth: "33%",
    },
    label: {
        margin: "8px 8px 8px 2px",
        fontSize: "12px",
        color: "#757575",
        fontWeight: "500",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
    },
    fieldContainer: {
        padding: "8px",
    },
    environmentValue: {
        padding: "8px 12px",
        backgroundColor: "#f5f5f5",
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        fontSize: "14px",
        color: "#333",
        minHeight: "20px",
        display: "flex",
        alignItems: "center",
    },
    qrButton: {
        backgroundColor: "#1976d2",
        color: "white",
        border: "none",
        padding: "12px 24px",
        borderRadius: "4px",
        fontSize: "16px",
        cursor: "pointer",
        fontWeight: "500",
        width: "200px",
        marginTop: "20px",
        "&:hover": {
            backgroundColor: "#1565c0",
        },
    },
    qrCodeContainer: {
        position: 'relative',
        marginTop: "20px",
        padding: "20px",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        backgroundColor: "#fafafa",
    },
    qrCodeInfo: {
        marginBottom: "15px",
        textAlign: "left",
        fontSize: "14px",
        lineHeight: "1.5",
    },
    qrCodeImage: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "200px",
        border: "2px dashed #ccc",
        borderRadius: "8px",
        backgroundColor: "white",
    },
    qrPlaceholder: {
        fontSize: "18px",
        color: "#666",
        textAlign: "center",
    },
    environmentSelect: {
        width: "200px",
        padding: "8px 0px",
        borderRadius: "4px",
        fontSize: "14px",
        backgroundColor: "white",
        color: "#333",
        cursor: "pointer",
        "&:focus": {
            outline: "none",
            borderColor: "#1976d2",
            boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.2)",
        },
    },
    downloadButton: {
      position: 'absolute',
      right: '30px',
      backgroundColor: "#1976d2",
      color: "white",
      border: "none",
      padding: "8px 14px",
      borderRadius: "4px",
      fontSize: "16px",
      cursor: "pointer",
      fontWeight: "500",
      marginTop: "20px",
      "&:hover": {
          backgroundColor: "#1565c0",
      },
    },
    clipboard: {
      cursor: 'pointer',
      marginLeft: '8px',
      display: 'inline-block',
      fontSize: '14px',
      color: '#9ca3af',
      fontWeight: '500',
      padding: '1px 8px',
      height: '18px',
      "&:hover": {
        backgroundColor: '#1976d2',
        color: '#fff',
        borderRadius: '4px',
      },
    },
    builderInfoCard: {
        padding: "12px 16px",
        marginBottom: 8,
        backgroundColor: "#f5f7fa",
        border: "1px solid #e0e4e8",
        borderLeft: "4px solid #1976d2",
        borderRadius: 4,
    },
    builderInfoTitle: {
        fontWeight: 600,
        marginBottom: 8,
        color: "#1a1a1a",
    },
    builderInfoList: {
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "6px 24px",
        margin: "0 0 0 12px",
        paddingLeft: 0,
        "& li": {
            margin: 0,
            maxWidth: "100%",
            wordBreak: "break-word",
        },
    },
}));

const EditToolbar = (props) => {
    const classes = useStyles();
    return (
        <Toolbar {...props} className={classes.toolbar}>
            <SaveButton disabled={props.pristine} />
            <DeleteWithConfirmButton />
        </Toolbar>
    );
};

EditToolbar.propTypes = {
  pristine: PropTypes.bool,
};

const BuilderPlatformDataSync = ({
  bestsellerId,
  builderPlatformId,
  onDataChange,
  onFailure,
}) => {
  useEffect(() => {
    if (!builderPlatformId) {
      onDataChange(null);
      return;
    }

    let isMounted = true;

    const fetchBuilderPlatformData = async () => {
      try {
        const response = await axios.get(
          `${baseURL.apiBaseUrl}/bestsellers/${bestsellerId}?builder_platform_id=${builderPlatformId}`
        );
        const builderPlatformData = response?.data?.data?.builder_platform_data;

        if (isMounted) {
          onDataChange(builderPlatformData?.discount_percentage ?? null);
        }
      } catch (error) {
        if (isMounted) {
          onDataChange(null);
          onFailure(error);
        }
      }
    };

    fetchBuilderPlatformData();

    return () => {
      isMounted = false;
    };
  }, [bestsellerId, builderPlatformId, onDataChange, onFailure]);

  return null;
};

BuilderPlatformDataSync.propTypes = {
  bestsellerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  builderPlatformId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onDataChange: PropTypes.func.isRequired,
  onFailure: PropTypes.func.isRequired,
};

const BuilderPlatformsInfoCard = () => {
    const classes = useStyles();
    const record = useRecordContext();

    if (!Array.isArray(record?.builder_platforms) || record.builder_platforms.length === 0) {
        return null;
    }

    return (
        <Grid item xs={12} style={{ margin: '8px'}}>
            <Paper className={classes.builderInfoCard} elevation={0}>
                <Typography
                    variant="subtitle2"
                    component="h3"
                    className={classes.builderInfoTitle}
                >
                    Builders with custom configuration
                </Typography>
                <Typography variant="body2" component="div" color="textSecondary">
                    <ul className={classes.builderInfoList}>
                        {record.builder_platforms.map((label, index) => (
                            <li key={`${label}-${index}`}>{label}</li>
                        ))}
                    </ul>
                </Typography>
            </Paper>
        </Grid>
    );
};

const BestSellerShow = (props) => {
    const classes = useStyles();
    const notify = useNotify();
    const qrRef = useRef()
    const [options, setOptions] = useState([])
    const [selectedEnvironment, setSelectedEnvironment] = useState(() => {
      const apiUrl = process.env.REACT_APP_API_BASE_URL || '';
      const isStaging = apiUrl.includes('staging');
      return isStaging ? 'lashbrook_staging' : 'lashbrook_prod';
    });
    const [qrCodeData, setQrCodeData] = useState(null);
    const [copied, setCopied] = useState(false);
    const [builderDiscountPercentage, setBuilderDiscountPercentage] = useState(null);

    const selectedOption = useMemo(
      () => options.find(option => option.value === selectedEnvironment),
      [options, selectedEnvironment]
    );
    const selectedBuilderPlatformId = selectedOption?.id;

    useEffect(() => {
      if (copied) {
        setTimeout(() => {
          setCopied(false);
        }, 1000);
      }
    }, [copied]);

    const ENVS = useMemo(() => {
      const apiUrl = process.env.REACT_APP_API_BASE_URL || '';
      const isStaging = apiUrl.includes('staging');

      if (isStaging) {
        return [
          {
            name: 'Lashbrook Staging',
            value: 'lashbrook_staging',
            url: `http://staging.lashbrookdesigns.com/shop/bestSeller/${props?.id}`,
          },
          {
            name: 'Helzberg Staging',
            value: 'helzberg_staging',
            url: `https://bhdw-005.dx.commercecloud.salesforce.com/on/demandware.store/Sites-Helzberg-Site/en_US/Product-ProductBuilder?ring_url=${encodeURIComponent('/helzberg/rings/profiles/D/widths/7/ring_materials/14KW?ring_finish=SATIN&finish_type=F&weight=P&size=10&feature_group=N&sleeve=NONE')}&bestSeller=${props?.id}`,
          }
        ];
      } else {
        return [
          {
            name: 'Lashbrook Production',
            value: 'lashbrook_prod',
            url: `https://lashbrookdesigns.com/shop/bestSeller/${props?.id}`,
          },
          {
            name: 'Helzberg Production',
            value: 'helzberg_prod',
            url: `https://helzberg.com/on/demandware.store/Sites-Helzberg-Site/en_US/Product-ProductBuilder?ring_url=${encodeURIComponent('/helzberg/rings/profiles/D/widths/7/ring_materials/14KW?ring_finish=SATIN&finish_type=F&weight=P&size=10&feature_group=N&sleeve=NONE')}&bestSeller=${props?.id}`,
          }
        ];
      }
    }, [props?.id]);

    useEffect(() => {
      const fetchBuilderPlatforms = async () => {
        const res = await axios.get(`${baseURL.apiBaseUrl}/builder-platform?page[number]=1&page[size]=1000&sort=store_name`)
        if(res.data.data.length > 0) {
          const apiUrl = process.env.REACT_APP_API_BASE_URL || '';
          const isStaging = apiUrl.includes('staging');
          const formattedOptions = res.data.data.map(item => {
            const ring_url = `apps/${item.subpath}/rings/profiles/D/widths/7/ring_materials/14KW?ring_finish=SATIN&finish_type=F&weight=P&size=10&feature_group=N&sleeve=NONE&bestSeller=${props?.id}`;
            let BASE_URL = '';

            if(isStaging) {
              BASE_URL = 'https://staging.ringdesignersstudio.com/';
            } else if(item.platform_name === 'shopify') {
              BASE_URL = `https://${item.base_url}`;
            } else {
              BASE_URL = 'https://ringdesignersstudio.com/';
            }
            return {
              name: item.store_name,
              value: item.subpath,
              id: item.id,
              url: `${BASE_URL}${ring_url}`,
            }
          })
          setOptions([...ENVS, ...formattedOptions])
        } else {
          setOptions([...ENVS]);
        }
      };
      fetchBuilderPlatforms();
    }, [ENVS, props?.id])

    const handleBuilderDataFailure = (error) => {
      notify(
        error?.response?.data?.message || "Failed to fetch builder platform data",
        { type: "warning" }
      );
    };

    const generateQRCode = () => {
        const url = options.find(option => option.value === selectedEnvironment)?.url;
        setCopied(false);
        setQrCodeData({
            environment: selectedEnvironment,
            url: url
        });
    }

    const downloadQR = () => {
      const svg = qrRef.current
      const svgData = new XMLSerializer().serializeToString(svg)
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()

      canvas.width = 256
      canvas.height = 256

      img.onload = () => {
        ctx.drawImage(img, 0, 0)
        const pngFile = canvas.toDataURL('image/png')

        const downloadLink = document.createElement('a')
        downloadLink.download = `BestSeller-${props?.id}.png`
        downloadLink.href = pngFile
        downloadLink.click()
      }

      img.src = 'data:image/svg+xml;base64,' + btoa(svgData)
    }

    return (
      <ThemeProvider theme={customTheme}>
        <Show {...props}>
          <div className={classes.root}>
            <Grid container spacing={2}>
              <BuilderPlatformsInfoCard />
              <Grid item xs={5} style={{ marginTop: '-30px'}}>
                <SimpleShowLayout>
                  <BuilderPlatformDataSync
                    bestsellerId={props?.id}
                    builderPlatformId={selectedBuilderPlatformId}
                    onDataChange={setBuilderDiscountPercentage}
                    onFailure={handleBuilderDataFailure}
                  />
                  <TextField source="magic_number" label="Magic Number" />
                  <TextField source="discount_percentage" label="Standard Discount Percentage"/>
                  <TextField source="kays_discount_percentage" label="Kay's Discount Percentage"/>
                  <div>
                    <div style={{ fontSize: '12px', color: '#0000008a', marginBottom: '4px'}}>Builder Discount Percentage</div>
                    <div style={{ fontSize: '14px', color: '#000000'}}>
                      {builderDiscountPercentage ?? "-"}
                    </div>
                  </div>
                  <TextField source="active" label="Active" />
                  <div>
                    <div className={classes.label}>Environment</div>
                    <select
                      className={classes.environmentSelect}
                      value={selectedEnvironment}
                      onChange={(e) => setSelectedEnvironment(e.target.value)}
                    >
                      {options.map(env => (
                        <option key={env.value} value={env.value}>
                          {env.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <button className={classes.qrButton} onClick={() => generateQRCode()}>
                      Generate QR Code
                    </button>
                  </div>
                </SimpleShowLayout>
              </Grid>
              <Grid item xs={7} style={{ padding: '20px'}}>
                {qrCodeData && (
                  <div className={classes.qrCodeContainer}>
                    <div className={classes.qrCodeInfo}>
                      <strong>Environment:</strong> {qrCodeData.environment}
                      <br />
                      <div style={{ display: 'flex', alignItems: 'center'}}>
                        <strong>URL:</strong>
                        <a href={qrCodeData.url} target="_blank" style={{ marginLeft: '8px'}}>Open in New Tab ↗</a>
                        <CopyToClipboard text={qrCodeData.url} onCopy={() => setCopied(true)}>
                          <div className={classes.clipboard}>
                            {copied ? <FaCheck size={14}/> : <FaCopy size={14}/>}
                          </div>
                        </CopyToClipboard>
                      </div>
                      <button onClick={downloadQR} className={classes.downloadButton}>
                        <FaDownload/>
                      </button>
                    </div>
                    <div className={classes.qrCodeImage}>
                      {/* QR Code image will be generated here */}
                      <div className={classes.qrPlaceholder}>
                        <QRCode value={qrCodeData.url} size={180} ref={qrRef} />
                      </div>
                    </div>
                  </div>
                )}
              </Grid>
            </Grid>
          </div>
        </Show>
        <Notification />
      </ThemeProvider>
    );
};

BestSellerShow.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default BestSellerShow;
